import { Component, h } from 'preact'
import Button from '../ui-components/Button'
import ColorPicker from './ColorPicker'
import { StylingConfigType } from './configurator'
import InputGroup from '../ui-components/InputGroup'

type CardStatusData = {
  key: string,
  checkbox?: {
    key: string,
    title: string
  }
}

type CardStatusType = 'default' | 'hover' | 'click'
type CardOptionsType = 'background' | 'title' | 'text' | 'border' | 'shadow'

type CardsTheme = {
  default: {
    background: CardStatusData,
    title: CardStatusData,
    text: CardStatusData,
    border: CardStatusData,
    shadow: CardStatusData
  },
  hover: {
    background: CardStatusData,
    title: CardStatusData,
    text: CardStatusData,
    border: CardStatusData,
    shadow: CardStatusData
  },
  click: {
    background: CardStatusData,
    title: CardStatusData,
    text: CardStatusData,
    border: CardStatusData,
    shadow: CardStatusData
  }
}

type GenericSetting = {
  key: string,
  label: string
}

const GENERIC_SETTINGS: Array<GenericSetting> = [
  {
    key: 'config-card-border-radius',
    label: 'Border radius'
  },
  {
    key: 'config-card-border-width',
    label: 'Border width'
  }
]

const CARDS: CardsTheme = {
  default: {
    background: {
      key: 'config-card-background-color'
    },
    title: {
      key: 'config-card-title-color',
      checkbox: {
        title: 'Use heading color',
        key: 'config-header-color'
      }
    },
    text: {
      key: 'config-card-text-color',
      checkbox: {
        title: 'Use text color',
        key: 'config--main-color-night'
      }
    },
    border: {
      key: 'config-card-border-color',
      checkbox: {
        title: 'Use border color',
        key: 'config--main-border-base-color'
      }
    },
    shadow: {
      key: 'config-card-shadow'
    }
  },
  hover: {
    background: {
      key: 'config-card-hover-background-color'
    },
    title: {
      key: 'config-card-hover-title-color',
      checkbox: {
        title: 'Use heading color',
        key: 'config-header-color'
      }
    },
    text: {
      key: 'config-card-hover-text-color',
      checkbox: {
        title: 'Use text color',
        key: 'config--main-color-night'
      }
    },
    border: {
      key: 'config-card-hover-border-color',
      checkbox: {
        title: 'Use border color',
        key: 'config--main-border-base-color'
      }
    },
    shadow: {
      key: 'config-card-hover-shadow'
    }
  },
  click: {
    background: {
      key: 'config-card-active-background-color'
    },
    title: {
      key: 'config-card-active-title-color',
      checkbox: {
        title: 'Use heading color',
        key: 'config-header-color'
      }
    },
    text: {
      key: 'config-card-active-text-color',
      checkbox: {
        title: 'Use text color',
        key: 'config--main-color-night'
      }
    },
    border: {
      key: 'config-card-active-border-color',
      checkbox: {
        title: 'Use border color',
        key: 'config--main-border-base-color'
      }
    },
    shadow: {
      key: 'config-card-hover-shadow'
    }
  }
}

const CARD_STATUSES: Array<CardStatusType> = ['default', 'hover', 'click']
const CARD_COLOR_OPTIONS: Array<CardOptionsType> = ['background', 'title', 'text', 'border', 'shadow']

interface Props {
  updatedStyling: StylingConfigType,
  defaultStyling: StylingConfigType,
  updatedColors: StylingConfigType,
  onUpdate: (updatedStyling: StylingConfigType) => void
}

interface State {
  enabledColorSelector: null | string,
  activeTab: CardStatusType
}

export default class Cards extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      enabledColorSelector: null,
      activeTab: 'default'
    }

    this.updateStyling = this.updateStyling.bind(this)
    this.resetStyling = this.resetStyling.bind(this)
  }

  updateStyling (key: string, color: string | null) {
    this.props.onUpdate({ ...this.props.updatedStyling, ...{ [key]: color } })
  }

  resetStyling () {
    this.props.onUpdate(this.props.defaultStyling)
  }

  render ({ updatedStyling, defaultStyling, updatedColors }: Props, { enabledColorSelector, activeTab }: State) {
    return (
      <div className='theme-configuration_cards'>
        <div className='box__pad theme-configuration_cards-setting'>
          <div className='theme-configuration_info-box'>
            <strong>✨ Which cards does this styling apply to?</strong>
            <p>Card style applies to all the widgets in card style, e.g. quick links, featured topics, community categories, knowledge base, etc</p>
          </div>
          <div className='theme-configuration_cards-generic-settings'>
            {
              GENERIC_SETTINGS.map((genericCardSetting: GenericSetting) => {
                return (
                  <div className={`theme-configuration_cards-generic-setting ${genericCardSetting.label.toLocaleLowerCase().split(' ').join('-')}`}>
                    <div className='theme-configuration_cards-generic-setting_name'>{ genericCardSetting.label }</div>
                    <div className='theme-configuration_cards-generic-setting_configuration'>
                      <div>
                        <InputGroup
                          title=''
                          type='number'
                          min={0}
                          onKeyUp={(e: {target: HTMLInputElement}) => this.updateStyling(genericCardSetting.key, e.target.value.toString() + 'px')}
                          value={updatedStyling[genericCardSetting.key]?.toString().replace('px', '')} />
                        <div>px</div>
                      </div>
                    </div>
                  </div>
                )
              })
            }
          </div>
          <div className='theme-configuration_tabs'>
            {
              CARD_STATUSES.map(tab => (
                <div
                  onClick={() => this.setState({ activeTab: tab })}
                  className={`${activeTab === tab ? 'active-tab' : ''} theme-configuration_tab`}>
                  { tab }
                </div>
              ))
            }
          </div>
          <ul className={`theme-configuration_card-list theme-configuration-${activeTab}`}>
            {
              CARD_COLOR_OPTIONS.map(cardColorOption => {
                const card = CARDS[activeTab][cardColorOption]

                if (cardColorOption === 'shadow') {
                  return (
                    <li className='theme-configuration-list-item_setting shadow'>
                      <label>
                        <div>{ `${cardColorOption} - ${activeTab}`}</div>
                        <div className='theme-configuration-list-item_setting_subtext'>Color x-axis y-axisradius, eg. rgba(0,0,0,0.2) 0  2px 4px</div>
                      </label>
                      <InputGroup
                        title=''
                        type='text'
                        placeholder='rgba(0,0,0,0.2) 0  2px 4px'
                        onKeyUp={(e: {target: HTMLInputElement}) => this.updateStyling(card.key, e.target.value)}
                        value={updatedStyling[card.key] || ''} />
                    </li>
                  )
                }

                return (
                  (
                    <li className={`theme-configuration-list-item_setting ${cardColorOption.toLocaleLowerCase().split(' ').join('-')}`}>
                      <label>{ `${cardColorOption} Color - ${activeTab}`}</label>
                      <ColorPicker
                        color={!!card.checkbox && updatedStyling[card.key] === null ? updatedColors[card.checkbox.key] : updatedStyling[card.key]}
                        disabled={!!card.checkbox && updatedStyling[card.key] === null}
                        colorKey={card.key}
                        disableColorSelector={() => this.setState({ enabledColorSelector: null })}
                        setColorSelector={() => this.setState({ enabledColorSelector: card.key })}
                        updateColor={(color: string) => this.updateStyling(card.key, color)}
                        isColorSelectorEnabled={enabledColorSelector === card.key}
                      />
                      {
                        CARDS[activeTab][cardColorOption].checkbox ? (
                          <div className='use-default'>
                            <InputGroup
                              inline
                              title={card.checkbox?.title}
                              type='checkbox'
                              checked={updatedStyling[card.key] === null ? 'checked' : ''}
                              onChangeCheckbox={(e: {target: HTMLInputElement}) => {
                                this.updateStyling(card.key, e.target.checked ? null : card.checkbox ? updatedColors[card.checkbox.key] : null)
                              }}
                            />
                          </div>
                        ) : null
                      }
                    </li>
                  )
                )
              })
            }
          </ul>
        </div>
        <div className='box__pad theme-configuration_cards-reset'>
          <Button warning onClick={this.resetStyling}>
            Reset to defaults
          </Button>
        </div>
      </div>
    )
  }
}
